import { cn, useShrinkTextToFit } from '@amusnet/react-ui'
import React, { MouseEvent, useEffect, useRef, useState } from 'react'

interface BetSliderButtonProps {
    key: number
    index: number
    money: string
    posX?: number
    onClick: () => void
    keyboardDownIndex: number
    className?: string
    backgroundImage: string
    actionLabel: string
    currency: string
    isActive: boolean
    isInteractive: boolean
    isScrolling: boolean
    isButtonFullyVisible: boolean
    shouldShowCurrency?: boolean
    disableOffsetAnimation?: boolean
    componentOpacity?: number
}

const BetSliderButton: React.FC<BetSliderButtonProps> = ({
    posX,
    onClick,
    className,
    backgroundImage,
    actionLabel,
    currency,
    isActive,
    isInteractive,
    isScrolling,
    isButtonFullyVisible,
    money,
    index,
    keyboardDownIndex,
    shouldShowCurrency = true,
    disableOffsetAnimation = false,
    componentOpacity = 1,
}) => {
    const amountRef = useRef<HTMLButtonElement>(null)
    const labelRef = useRef<HTMLButtonElement>(null)
    const width = 136
    const [isPointerDown, setIsPointerDown] = useState(false)
    const pointerDownPosition = useRef({ x: 0, y: 0 })

    useShrinkTextToFit({
        refresh: [money, componentOpacity],
        designFontSize: 46,
        bounds: { width: width - 4 },
        textRef: amountRef,
    })

    useShrinkTextToFit({
        refresh: [actionLabel, componentOpacity],
        designFontSize: 24,
        bounds: { width: width - 20, height: 24 },
        textRef: labelRef,
    })

    useEffect(() => {
        setIsPointerDown(index === keyboardDownIndex)
    }, [keyboardDownIndex])

    const handleContextMenuAndWheel = (e: MouseEvent) => {
        e.preventDefault()
    }

    const handlePointerDown = (e: MouseEvent) => {
        if (e.button !== 0) {
            // If not left click exit
            e.preventDefault()
            e.stopPropagation()

            return
        }

        setIsPointerDown(isButtonFullyVisible)
        pointerDownPosition.current = { x: e.clientX, y: e.clientY }
    }

    const handlePointerUp = (e: MouseEvent) => {
        setIsPointerDown(false)

        if (e.button !== 0) {
            // If not left click exit
            e.preventDefault()
            e.stopPropagation()

            return
        }

        // Check if the pointer up event was meant to be a click to avoid
        // conflicting with pointer up on swiping/dragging quickly
        const dx = Math.abs(e.clientX - pointerDownPosition.current.x)
        const dy = Math.abs(e.clientY - pointerDownPosition.current.y)
        const distance = Math.sqrt(dx * dx + dy * dy)
        const CLICK_THRESHOLD = 5

        if (distance < CLICK_THRESHOLD) onClick()
    }

    const handlePointerOut = () => {
        if (isPointerDown) {
            setIsPointerDown(false)
        }
    }

    return (
        <button
            id={`bet-${Math.round(+money * 100)}`}
            disabled={!isInteractive}
            onContextMenu={handleContextMenuAndWheel}
            onWheel={handleContextMenuAndWheel}
            onPointerDown={handlePointerDown}
            onPointerUp={handlePointerUp}
            onPointerOut={handlePointerOut}
            className={cn(
                className,
                !isScrolling && isPointerDown && !disableOffsetAnimation && 'transform: translate-y-[3px]'
            )}
            style={{ width: `${width}px`, height: '96px', left: posX, pointerEvents: isInteractive ? 'auto' : 'none' }}
        >
            {/* Background Image */}
            <img
                src={backgroundImage}
                alt="Bet Button Background"
                className="absolute inset-0 w-full h-full object-cover"
                draggable="false"
            />

            {shouldShowCurrency && (
                <span
                    className={cn(
                        isActive ? 'text-[#FFD100]' : 'text-[#cacaca]',
                        'font-bold text-[18px] leading-[16px] mt-[7px] z-10 uppercase'
                    )}
                >
                    {currency}
                </span>
            )}

            {/* Amount */}
            <span ref={amountRef} className="font-black text-[46px] leading-[44px] mt-[1px] z-10 whitespace-nowrap">
                {money}
            </span>

            {/* Label */}
            <span ref={labelRef} className="text-[#FFD100] font-bold text-[24px] leading-none mt-0 z-10">
                {actionLabel}
            </span>
        </button>
    )
}

export default BetSliderButton
